// Pure diff between two supplier-response version snapshots (append-only
// rows from brief_response_versions). Powers the Fusion-facing edit trail
// behind "View Response". Whole-field text diffs only (no word-level, per
// spec). Line items are matched by trimmed description; totals compare the
// grand (inc-VAT) figure; documents compare by file name. Returns raw
// numbers/strings — the view formats them with its own GBP/date helpers.

const norm = (s) => (s == null ? '' : String(s)).trim();

const grossOf = (v) => {
  const net = Number(v?.total_price) || 0;
  const rate = Number(v?.vat_rate) || 0;
  return net + (net * rate) / 100;
};

const itemsOf = (v) => (Array.isArray(v?.line_items) ? v.line_items : []);
const docsOf = (v) => (Array.isArray(v?.document_refs) ? v.document_refs : []);

// Match by description. Each prev item can match at most one curr item, so
// duplicate descriptions degrade gracefully (first-unused wins). Unchanged
// items are omitted; only add / remove / price-change rows are returned.
function diffLineItems(prev, curr) {
  const rows = [];
  const prevItems = itemsOf(prev);
  const currItems = itemsOf(curr);
  const usedPrev = new Set();
  for (const ci of currItems) {
    const name = norm(ci.description) || 'Untitled item';
    const pIdx = prevItems.findIndex(
      (pi, i) => !usedPrev.has(i) && norm(pi.description) === norm(ci.description)
    );
    if (pIdx === -1) {
      rows.push({ tag: 'add', name, now: Number(ci.line_total) || 0 });
    } else {
      usedPrev.add(pIdx);
      const was = Number(prevItems[pIdx].line_total) || 0;
      const now = Number(ci.line_total) || 0;
      if (was !== now) rows.push({ tag: 'chg', name, was, now });
    }
  }
  prevItems.forEach((pi, i) => {
    if (!usedPrev.has(i)) {
      rows.push({ tag: 'rem', name: norm(pi.description) || 'Untitled item', was: Number(pi.line_total) || 0 });
    }
  });
  return rows;
}

const FIELD_DEFS = [
  { key: 'payment_schedule', label: 'Payment Schedule / Terms', type: 'text' },
  { key: 'salient_points', label: 'Salient Points', type: 'text' },
  { key: 'comments', label: 'Comments, Restrictions & Caveats', type: 'text' },
  { key: 'deadline_date', label: 'Confirmation Required By', type: 'date' },
];

function diffFields(prev, curr) {
  const out = [];
  for (const f of FIELD_DEFS) {
    const was = prev?.[f.key] ?? null;
    const now = curr?.[f.key] ?? null;
    if (norm(was) !== norm(now)) out.push({ field: f.label, type: f.type, was, now });
  }
  return out;
}

function diffDocuments(prev, curr) {
  const rows = [];
  const key = (d) => norm(d.file_name) || norm(d.file_url);
  const prevDocs = docsOf(prev);
  const currDocs = docsOf(curr);
  const prevKeys = new Set(prevDocs.map(key));
  const currKeys = new Set(currDocs.map(key));
  for (const d of currDocs) if (!prevKeys.has(key(d))) rows.push({ tag: 'add', name: d.file_name || d.file_url });
  for (const d of prevDocs) if (!currKeys.has(key(d))) rows.push({ tag: 'rem', name: d.file_name || d.file_url });
  return rows;
}

// Diff `curr` against the version immediately before it (`prev`). When
// `prev` is null (the original version), there's nothing to compare.
export function diffVersions(prev, curr) {
  if (!curr) return null;
  if (!prev) return { isOriginal: true, lineItems: [], totals: null, fields: [], documents: [] };
  const prevGross = grossOf(prev);
  const currGross = grossOf(curr);
  return {
    isOriginal: false,
    lineItems: diffLineItems(prev, curr),
    totals: prevGross !== currGross ? { was: prevGross, now: currGross } : null,
    fields: diffFields(prev, curr),
    documents: diffDocuments(prev, curr),
  };
}

export function hasChanges(diff) {
  if (!diff || diff.isOriginal) return false;
  return diff.lineItems.length > 0 || !!diff.totals || diff.fields.length > 0 || diff.documents.length > 0;
}
